// Phase 1A Step 34 — Landing page root composition.
// Verbatim port from handoff/landing-reference/app.jsx App component.
// Hosts the calculator state (stake/weeks/mode/currency/pricingMode) and
// composes the 12 sections in their handoff-locked order.
//
// The handoff's TweaksPanel (design-time-only debug controls) is deliberately
// stripped from production per handoff/landing-reference/README.md "How to
// port" step 6.
//
// Tokens are scoped to .sz-landing-root so the handoff color palette renders
// at ≥99% visual parity without colliding with the app's --sz-* token system.

import { useState } from "react";
import { AgentSwarm } from "./AgentSwarm.tsx";
import { Big6Strip } from "./Big6Strip.tsx";
import { EdgeSection } from "./EdgeSection.tsx";
import { FeatureMatrix } from "./FeatureMatrix.tsx";
import { FinalCTA } from "./FinalCTA.tsx";
import { Hero } from "./Hero.tsx";
import { type Currency } from "./landing-data.ts";
import { LandingFooter } from "./LandingFooter.tsx";
import { LandingNav } from "./LandingNav.tsx";
import { LedgerSection } from "./LedgerSection.tsx";
import { LegalStrip } from "./LegalStrip.tsx";
import { ModulesRoadmap } from "./ModulesRoadmap.tsx";
import { MultiMarketDashboard } from "./MultiMarketDashboard.tsx";
import { PlatformUnderneath } from "./PlatformUnderneath.tsx";
import { Pricing } from "./Pricing.tsx";
import { RolloverTable } from "./RolloverTable.tsx";
import { SikaSection } from "./SikaSection.tsx";
import { StatsSection } from "./StatsSection.tsx";

// Locked defaults per handoff/landing-reference/app.jsx TWEAK_DEFAULTS.
// Hero math (GH₵85,598 protected pocket) depends on these — do not edit.
const DEFAULTS = {
  stake: 300,
  weeks: 10,
  odds: 3,
  takeout: 0.5,
  mode: "protected" as const,
  headline: "COMPOUND, DON'T GAMBLE.",
  currency: "GHS" as Currency,
  pricingMode: "tiers" as const,
};

export function LandingPage(): JSX.Element {
  const [stake, setStake] = useState<number>(DEFAULTS.stake);
  const [weeks, setWeeks] = useState<number>(DEFAULTS.weeks);
  const [currency, setCurrency] = useState<Currency>(DEFAULTS.currency);
  const [mode, setMode] = useState<"protected" | "full">(DEFAULTS.mode);
  const [pricingMode, setPricingMode] = useState<"tiers" | "bundles">(DEFAULTS.pricingMode);

  const symbol = currency === "GHS" ? "GH₵" : "₦";
  const odds = DEFAULTS.odds;
  const takeout = DEFAULTS.takeout;

  return (
    <div className="sz-landing-root">
      <LandingNav />
      <Hero
        stake={stake}
        setStake={setStake}
        weeks={weeks}
        setWeeks={setWeeks}
        odds={odds}
        takeout={takeout}
        symbol={symbol}
        headline={DEFAULTS.headline}
        mode={mode}
        setMode={setMode}
      />
      <SikaSection />
      <AgentSwarm />
      <LedgerSection />
      <PlatformUnderneath />
      <RolloverTable stake={stake} weeks={weeks} odds={odds} takeout={takeout} symbol={symbol} />
      <EdgeSection />
      <Big6Strip />
      <MultiMarketDashboard />
      <StatsSection />
      <ModulesRoadmap />
      <FeatureMatrix />
      <Pricing
        currency={currency}
        setCurrency={setCurrency}
        pricingMode={pricingMode}
        setPricingMode={setPricingMode}
      />
      <LegalStrip />
      <FinalCTA stake={stake} weeks={weeks} odds={odds} takeout={takeout} symbol={symbol} />
      <LandingFooter />
    </div>
  );
}
